// Renders an order's lifecycle_stage as a coloured pill.
// One entry per value in the order_lifecycle_stage enum (business-rules §3).
//
// Note on `in_review`: that single enum value covers two user-facing states —
// DU is working on the order vs admin is reviewing what the DU submitted.
// If ownerTeam is provided, we pick the clearer label; otherwise fall back
// to the generic "In review".

const PALETTE = {
  intake:                          { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Intake' },
  data_incomplete:                 { bg: 'bg-amber-100',  text: 'text-amber-800',  label: 'Data incomplete' },
  reship_unlinked:                 { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Reship — link' },
  blocked_troublesome_customer:    { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Blocked' },
  in_review:                       { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'In review' },
  validation_pending:              { bg: 'bg-orange-100', text: 'text-orange-900', label: 'Validation pending' },
  ready_for_dispatch:              { bg: 'bg-emerald-100',text: 'text-emerald-800',label: 'Ready to dispatch' },
  with_vendor:                     { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'With vendor' },
  in_transit:                      { bg: 'bg-sky-100',    text: 'text-sky-800',    label: 'In transit' },
  partially_shipped:               { bg: 'bg-amber-100',  text: 'text-amber-800',  label: 'Partially shipped' },
  on_hold:                         { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'On hold' },
  needs_attention:                 { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Needs attention' },
  completed:                       { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Completed' },
  cancelled:                       { bg: 'bg-rose-100',   text: 'text-rose-800',   label: 'Cancelled' },
  delivered_unpaid:                { bg: 'bg-red-200',    text: 'text-red-900',    label: 'Delivered — unpaid' },
};

const IN_REVIEW_BY_OWNER = {
  dispatch_user:  { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'With user' },
  dispatch_admin: { bg: 'bg-violet-100', text: 'text-violet-800', label: 'Awaiting approval' },
  ops:            { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'In review (Ops)' },
};

// Issue #23 — an original order that has been reshipped reads "Reshipped"
// instead of its underlying stage (Kalpana: "no need to show the underlying
// status as Delivered – Reshipped"). Derived, not a stored stage: the order
// really is still in transit / delivered / returned underneath, which is why
// the true label stays in the tooltip and OrderDetail shows it in full.
const RESHIPPED = { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Reshipped' };

export default function LifecycleBadge({ stage, ownerTeam, hasReship = false }) {
  let p = PALETTE[stage] || { bg: 'bg-gray-100', text: 'text-gray-700', label: stage || '—' };
  if (stage === 'in_review' && ownerTeam && IN_REVIEW_BY_OWNER[ownerTeam]) {
    p = IN_REVIEW_BY_OWNER[ownerTeam];
  }
  const underlying = p.label;
  if (hasReship) p = RESHIPPED;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.bg} ${p.text}`}
      title={hasReship ? `Reshipped — original is ${underlying}` : undefined}
    >
      {p.label}
    </span>
  );
}
